import axios from "utils/axiosConfig";
import { useState } from "react";
import CandidateService from "services/CandidateService";
import useNotifications from "hooks/useNotifications";
import { useDispatch } from "react-redux";
import { currentCandidate } from "reducers/candidate/currentCandidateSlice";

export const useUpdateCandidateById = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteNoteLoading, setIsDeleteNoteLoading] = useState(false);
    const [isDeleteSuccessfull, setIsDeleteSuccessfull] = useState(false);
    const [isEditNoteSuccessfull, setIsEditNoteSuccessfull] = useState(false);
    const [isEditNoteLoading, setIsEditNoteLoading] = useState(false);
    const [isLanguagesLoading, setIsLanguagesLoading] = useState(false);
    const [isLanguagesSuccessfull, setIsLanguagesSuccessfull] = useState(false);
    const [requestSuccessful, setRequestSuccessful] = useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();
    const dispatch = useDispatch();

    const refetchCandidateToStore = async (id) => {
        const { data, error } = await CandidateService.fetchCandidateById(id);
        if (!error && data) {
            dispatch(currentCandidate(data));
        }
        return data;
    };

    const updateCandidateById = async (id, updatedData) => {
        setIsLoading(true);
        try {
            await axios.patch(
                `${CandidateService.dataBaseURL}/${id}`,
                updatedData,
            );
            const candidate = await refetchCandidateToStore(id);
            setRequestSuccessful(true);
            renderNotification("Updated successfully");
            setIsLoading(false);
            return candidate;
        } catch (error) {
            const errorData = error?.response?.data;
            const statusCode = error?.response?.status;
            let errorMessage = "Error updating candidate";

            if (statusCode === 500) {
                errorMessage = "Oops! Looks like there was a server error";
            } else if (errorData && typeof errorData === "object") {
                const fieldErrors = Object.entries(errorData)
                    .map(
                        ([key, message]) =>
                            `Error - ${key.charAt(0).toUpperCase() + key.slice(1)}: ${message}`,
                    )
                    .join(", ");

                if (fieldErrors) {
                    errorMessage = fieldErrors;
                }
            } else if (typeof errorData === "string") {
                errorMessage = errorData;
            }

            setRequestSuccessful(false);
            renderErrorNotification(errorMessage);
            console.error("Error updating candidate:", error);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    };

    const deleteNote = async (candidateId, noteId) => {
        const endpoint = `${CandidateService.dataBaseURL}/${candidateId}/notes/${noteId}`;
        try {
            setIsDeleteNoteLoading(true);
            await axios.delete(endpoint);
            await refetchCandidateToStore(candidateId);
            setIsDeleteNoteLoading(false);
            setIsDeleteSuccessfull(true);
        } catch (error) {
            renderErrorNotification(error.response.data);
            throw new Error("Failed to delete note");
        } finally {
            setIsDeleteNoteLoading(false);
        }
    };

    const editNote = async (endpointParams, updateData) => {
        const { candidateId, noteId } = endpointParams;
        setIsEditNoteLoading(true);
        const endpoint = `${CandidateService.dataBaseURL}/${candidateId}/notes/${noteId}`;
        try {
            await axios.patch(endpoint, updateData);
            await refetchCandidateToStore(candidateId);
            setIsEditNoteLoading(false);
            setIsEditNoteSuccessfull(true);
            renderNotification("Updated successfully");
        } catch (error) {
            renderErrorNotification(error.response.data);
            throw new Error("Failed to edit note");
        } finally {
            setIsDeleteNoteLoading(false);
        }
    };

    const updateLanguages = async (id, updatedData) => {
        setIsLanguagesLoading(true);
        try {
            await axios.post(
                `${CandidateService.dataBaseURL}/${id}/languages`,
                updatedData,
            );
            const candidate = await refetchCandidateToStore(id);
            setIsLanguagesSuccessfull(true);
            renderNotification("Updated successfully");
            setIsLanguagesLoading(false);
            return candidate;
        } catch (error) {
            setIsLanguagesSuccessfull(false);
            renderErrorNotification(`Error updating candidate: ${error}`);
            console.error("Error updating candidate:", error);
            setIsLanguagesLoading(false);
            throw new Error("Error updating candidate");
        }
    };

    return {
        updateCandidateById,
        deleteNote,
        editNote,
        updateLanguages,
        isLanguagesLoading,
        isLanguagesSuccessfull,
        isEditNoteLoading,
        isEditNoteSuccessfull,
        isDeleteNoteLoading,
        isDeleteSuccessfull,
        isLoading,
        requestSuccessful,
    };
};
